package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initThePathYouSignedPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ThePathYouSigned.md", FrontMatterElement.Builder { addScalar("title", "The path you signed is not the path you sent"); addScalar("searchTitle", "AWS SigV4 and presigned URLs in Kotlin/Native"); addScalar("date", "2026-08-17"); addScalar("description", "Writing an S3 client for Kotlin/Native. Three things that only turned up once a real request went out, and one that a green test suite was hiding."); addScalar("library", "s3kn"); }))
}

@Page("/blog/the-path-you-signed")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ThePathYouSignedPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A service on Kotlin/Native has nothing to talk to object storage with. The AWS SDK for Kotlin publishes JVM artefacts only; binding ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aws-c-s3")
        }
        org.jetbrains.compose.web.dom.Text(" through cinterop drags five C libraries and their build onto every target — and you still have to understand SigV4 to sign a URL.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/s3kn") {
            org.jetbrains.compose.web.dom.Text("s3kn")
        }
        org.jetbrains.compose.web.dom.Text(" writes the protocol out instead: the signature, and seven HTTP requests. Most of it went the way the specification said it would. These three did not.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("libcurl-rewrites-it-after-you-sign") }) {
        org.jetbrains.compose.web.dom.Text("libcurl rewrites it after you sign")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("S3 is unusual among AWS services: it signs the request path ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("verbatim")
        }
        org.jetbrains.compose.web.dom.Text(". No dot segments removed, no second round of percent-encoding. So a key like ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("a/./b")
        }
        org.jetbrains.compose.web.dom.Text(" is signed exactly as written — which is the whole reason the signer has two modes rather than one.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Getting that right took a day. Discovering it did not matter took a live request.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The key ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("a/./b")
        }
        org.jetbrains.compose.web.dom.Text(" left as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("a/b")
        }
        org.jetbrains.compose.web.dom.Text(". libcurl normalises the path per RFC 3986 before sending, the Ktor engine never sets ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CURLOPT_PATH_AS_IS")
        }
        org.jetbrains.compose.web.dom.Text(", and the only symptom is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SignatureDoesNotMatch")
        }
        org.jetbrains.compose.web.dom.Text(" — a response that names no header, no character and no field.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The unit test asserting the encoded path was green the entire time. It shows what the URL builder produced, not what reaches the socket, and no amount of staring at it would have helped. What proved it was running the same request through a second engine: on the JVM the path arrives intact and the server complains about the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("name")
        }
        org.jetbrains.compose.web.dom.Text(" instead of the signature. Two engines disagreeing is a fact; one engine failing is a hypothesis.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The library now refuses such a key outright. S3 accepts them and the JVM engines deliver them, so this forbids something genuinely possible — but a silent failure on the platform the library is built for is worse than a refusal everywhere, and the error says what is wrong in a sentence.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("when-there-are-no-vectors-ask-somebody-else-s-implementation") }) {
        org.jetbrains.compose.web.dom.Text("When there are no vectors, ask somebody else's implementation")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("AWS publishes 34 test vectors for SigV4, and they are excellent: each case ships the raw request, the canonical request, the string to sign and the resulting header. Four of them are skipped by botocore's own runner — one because a general HTTP parser cannot read a request line containing a space, three because parsing a query into a map loses repeated names. Neither limit belongs to the algorithm, so all 34 run here.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What AWS does not publish is vectors for the two rules S3 adds: the verbatim path, and presigned URLs. Writing those expectations by hand is a trap — you end up testing your own reading of the specification against itself, twice, and a misreading passes both times.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So they are generated from botocore by a script committed next to them. Not official, and only as trustworthy as the reference implementation — but an ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("independent")
        }
        org.jetbrains.compose.web.dom.Text(" implementation rather than a restatement of my own behaviour. The sharpest of them is a plain table of keys and their encoded forms, produced by Python's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("quote")
        }
        org.jetbrains.compose.web.dom.Text(": agreeing with it checks how I read the rule, not that I can repeat it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And a green vector suite proves nothing on its own. Switching the signer to the other path mode has to fail 10 of the 34; sorting the presigned query differently has to fail every presign case. Until a test fails when you break the thing it covers, it is decoration.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-protocol-two-encodings") }) {
        org.jetbrains.compose.web.dom.Text("One protocol, two encodings")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A listing asks S3 to encode the keys it returns, because an object key may hold characters XML 1.0 cannot represent at all. Fine. Then a key containing ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("my dir")
        }
        org.jetbrains.compose.web.dom.Text(" came back as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("my+dir")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The request path encodes a space as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("%20")
        }
        org.jetbrains.compose.web.dom.Text(", per RFC 3986. The listing response encodes it as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+")
        }
        org.jetbrains.compose.web.dom.Text(", which is form encoding. Same protocol, same request, two conventions — and botocore decodes those responses with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unquote_plus")
        }
        org.jetbrains.compose.web.dom.Text(", so the server was behaving exactly as the SDKs expect. The wrong assumption was mine: that one decoder covers both directions.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It is unambiguous, as it happens — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+")
        }
        org.jetbrains.compose.web.dom.Text(" is not an unreserved character, so a literal one arrives as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("%2B")
        }
        org.jetbrains.compose.web.dom.Text(" — but that is a sentence I could have written confidently and wrongly. Instead there is a key called ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("a+b")
        }
        org.jetbrains.compose.web.dom.Text(" sitting next to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("my dir")
        }
        org.jetbrains.compose.web.dom.Text(" in the live tests.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same afternoon produced a smaller one: botocore's comment lists ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ContinuationToken")
        }
        org.jetbrains.compose.web.dom.Text(" among the encoded fields, and its code does not decode it. The code is right — the token is opaque and travels back verbatim, so decoding it would send S3 something it never issued. When a reference implementation contradicts itself, the code is the reference.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rest-of-it") }) {
        org.jetbrains.compose.web.dom.Text("The rest of it")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("put")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("get")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("delete")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("head")
            }
            org.jetbrains.compose.web.dom.Text(", listing, multipart upload, presigned URLs. Nothing else.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Bodies stream in both directions; a five-gigabyte object is never held whole. The body of a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("get")
            }
            org.jetbrains.compose.web.dom.Text(" is handed to a block rather than returned, because it only lives as long as the connection does.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One encoder for the object key, called by both the signer and the URL builder. Two that differ by a character produce a rejection that explains nothing.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The credential scope date is a substring of the timestamp, not a second calculation. Computing it separately is correct 86 399 seconds a day.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Multipart aborts on every path that can fail, including cancellation — the abort runs under ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("NonCancellable")
            }
            org.jetbrains.compose.web.dom.Text(", or it would be cancelled along with everything else and leave precisely the parts it exists to remove.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("No cinterop anywhere. The crypto is pure Kotlin, and TLS arrives inside the engine's own klib. Which is why one Linux job publishes every artefact, macOS klibs included.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-a-container-still-needs") }) {
        org.jetbrains.compose.web.dom.Text("What a container still needs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("TLS is inside ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-client-curl")
        }
        org.jetbrains.compose.web.dom.Text(" — it carries its own libcurl and OpenSSL, so nothing has to be installed for it. The root certificates are another matter: they are read from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/etc/ssl/certs")
        }
        org.jetbrains.compose.web.dom.Text(" at runtime, and a slim image has none.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("\"Works on my machine\" proves nothing there, because my machine has certificates. So the check builds the image twice and requires opposite outcomes: with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ca-certificates")
        }
        org.jetbrains.compose.web.dom.Text(" it must reach AWS and get an HTTP status back, without them it must fail. A test that can only pass is not a test.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/s3kn") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/s3kn")
        }
    }
}
